import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdWebhooksId,
  getAdminV1StoresStoreIdWebhooks,
  getAdminV1StoresStoreIdWebhooksEvents,
  getAdminV1StoresStoreIdWebhooksId,
  getAdminV1StoresStoreIdWebhooksIdDeliveries,
  patchAdminV1StoresStoreIdWebhooksId,
  postAdminV1StoresStoreIdWebhooks,
  postAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliver,
  postAdminV1StoresStoreIdWebhooksIdRotateSecret,
  postAdminV1StoresStoreIdWebhooksIdTest,
  type GetAdminV1StoresStoreIdWebhooksIdDeliveries200,
  type PatchAdminV1StoresStoreIdWebhooksIdBody,
  type PostAdminV1StoresStoreIdWebhooksBody,
  type WebhookDelivery,
  type WebhookEndpoint,
  type WebhookEndpointWithSecret,
} from '@nucleus-commerce/api-client/admin';

/**
 * Delivery log params as `listParams` builds them from the route's search: `sort`, the flat
 * filter params (`status=failed,retrying`, `createdAt_gte=2026-09-01`), `cursor`, `limit`.
 */
export type DeliveryListParams = ListParams;

/**
 * Webhook endpoints of one store. The signing secret exists in the create and rotate responses
 * only; everything else returns the endpoint without it.
 */
export const webhooksApi = {
  events: (storeId: string): Promise<string[]> =>
    getAdminV1StoresStoreIdWebhooksEvents({ storeId }).then((r) => r.items),
  list: (storeId: string): Promise<WebhookEndpoint[]> =>
    getAdminV1StoresStoreIdWebhooks({ storeId }).then((r) => r.items),
  get: (storeId: string, id: string): Promise<WebhookEndpoint> =>
    getAdminV1StoresStoreIdWebhooksId({ storeId, id }),
  create: (
    storeId: string,
    body: PostAdminV1StoresStoreIdWebhooksBody,
  ): Promise<WebhookEndpointWithSecret> => postAdminV1StoresStoreIdWebhooks({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdWebhooksIdBody,
  ): Promise<WebhookEndpoint> => patchAdminV1StoresStoreIdWebhooksId({ storeId, id }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdWebhooksId({ storeId, id }),
  rotateSecret: (storeId: string, id: string): Promise<WebhookEndpointWithSecret> =>
    postAdminV1StoresStoreIdWebhooksIdRotateSecret({ storeId, id }),
  test: (storeId: string, id: string): Promise<WebhookDelivery> =>
    postAdminV1StoresStoreIdWebhooksIdTest({ storeId, id }),
  deliveries: (
    storeId: string,
    id: string,
    params: DeliveryListParams,
  ): Promise<GetAdminV1StoresStoreIdWebhooksIdDeliveries200> =>
    getAdminV1StoresStoreIdWebhooksIdDeliveries({ storeId, id }, params),
  redeliver: (storeId: string, id: string, deliveryId: string): Promise<WebhookDelivery> =>
    postAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliver({ storeId, id, deliveryId }),
};
